// État : Journaux comptables (lecture seule, temps réel).
// Détail des écritures regroupées par journal (Ventes, Achats, Banque, Caisse, OD, Salaires).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { telechargerExport } from "@/lib/exportFichier";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { Layers, Download, Printer, Loader2, ChevronLeft, ChevronRight, FileText } from "lucide-react";

const fmt = (n) => (n ? Math.round(n).toLocaleString("fr-FR") : "—");
const anneeCourante = new Date().getFullYear();
const JOURNAUX = [
  { code: "tous", label: "Tous" }, { code: "VT", label: "Ventes" }, { code: "AC", label: "Achats" },
  { code: "BQ", label: "Banque" }, { code: "CA", label: "Caisse" },
  { code: "OD", label: "Opérations diverses" }, { code: "ODS", label: "Salaires" },
];

export default function Journaux() {
  const { company } = useAuth();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [journal, setJournal] = useState("tous");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const params = useCallback(() => ({
    exercice: parseInt(exercice, 10),
    date_debut: dateDebut || undefined,
    date_fin: dateFin || undefined,
    journal: journal === "tous" ? undefined : journal,
  }), [exercice, dateDebut, dateFin, journal]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/etats/journaux", { params: { ...params(), page, page_size: 10 } });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [params, page]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setPage(1); }, [exercice, dateDebut, dateFin, journal]);

  const exporter = async (format) => {
    try {
      await telechargerExport("/etats/journaux/export", params(), format, `journaux_${exercice}`);
    } catch {
      toast.error("L'export a échoué. Veuillez réessayer.");
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total_journaux / data.page_size)) : 1;

  return (
    <div className="fenua-fade-up" data-testid="page-journaux">
      <div className="no-print flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Layers className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Livres de journaux</h1>
            <p className="text-sm text-[#1D3D3D]/70">Détail des écritures regroupées par journal.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" data-testid="export-csv" onClick={() => exporter("csv")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="secondary" data-testid="export-xls" onClick={() => exporter("xls")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> XLS
          </Button>
          <Button variant="secondary" data-testid="export-pdf" onClick={() => exporter("pdf")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button data-testid="bouton-imprimer" onClick={() => window.print()}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <Printer className="h-4 w-4 mr-1" /> Imprimer
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="no-print mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Du</Label>
          <DatePicker testId="filtre-date-debut" value={dateDebut} onChange={setDateDebut} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Au</Label>
          <DatePicker testId="filtre-date-fin" value={dateFin} onChange={setDateFin} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Journal</Label>
          <Select value={journal} onValueChange={setJournal}>
            <SelectTrigger data-testid="filtre-journal" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {JOURNAUX.map((j) => <SelectItem key={j.code} value={j.code}>{j.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Contenu imprimable */}
      <div className="mt-6 zone-impression">
        <div className="bg-white border border-[#4A8A8A]/20 rounded-t-lg px-5 py-3">
          <p className="font-bold text-[#1D3D3D]">{company?.raison_sociale}</p>
          <p className="text-xs text-[#1D3D3D]/60">Livres de journaux — Exercice {exercice}</p>
        </div>

        {chargement ? (
          <div className="bg-white border-x border-b border-[#4A8A8A]/20 rounded-b-lg p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : !data || data.journaux.length === 0 ? (
          <p className="bg-white border-x border-b border-[#4A8A8A]/20 rounded-b-lg p-10 text-center text-sm text-[#1D3D3D]/60">Aucun mouvement pour ces critères.</p>
        ) : (
          <div className="space-y-4" data-testid="journaux-liste">
            {data.journaux.map((j) => (
              <div key={j.code} data-testid={`journal-${j.code}`} className="bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden">
                <div className="bg-[#2D5A5A] text-white px-4 py-2 font-semibold text-sm flex items-center justify-between">
                  <span><span className="font-mono">{j.code}</span> — {j.nom}</span>
                  {!j.equilibre && <span className="text-xs font-normal text-amber-200">Journal déséquilibré</span>}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                      <th className="px-4 py-2 font-semibold">Date</th>
                      <th className="px-4 py-2 font-semibold">Pièce</th>
                      <th className="px-4 py-2 font-semibold">Compte</th>
                      <th className="px-4 py-2 font-semibold">Libellé</th>
                      <th className="px-4 py-2 font-semibold text-right">Débit</th>
                      <th className="px-4 py-2 font-semibold text-right">Crédit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#4A8A8A]/10">
                    {j.lignes.map((l, i) => (
                      <tr key={i} className="hover:bg-[#E8F4F4]/40">
                        <td className="px-4 py-2 text-[#1D3D3D]/80 whitespace-nowrap">{l.date}</td>
                        <td className="px-4 py-2 font-mono text-[#4A8A8A]">{l.reference || "—"}</td>
                        <td className="px-4 py-2 font-mono text-[#1D3D3D]/80 whitespace-nowrap">{l.compte}</td>
                        <td className="px-4 py-2 text-[#1D3D3D]">{l.libelle}</td>
                        <td className="px-4 py-2 text-right">{fmt(l.debit)}</td>
                        <td className="px-4 py-2 text-right">{fmt(l.credit)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#E8F4F4] font-bold text-[#1D3D3D]">
                      <td className="px-4 py-2" colSpan={4}>Sous-total</td>
                      <td className="px-4 py-2 text-right">{fmt(j.total_debit)}</td>
                      <td className="px-4 py-2 text-right">{fmt(j.total_credit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {data && data.journaux.length > 0 && (
        <div data-testid="journaux-recap" className="mt-4 zone-impression bg-[#1D3D3D] text-white rounded-lg px-5 py-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm font-semibold">Récapitulatif global (tous journaux)</p>
          <div className="flex items-center gap-8">
            <div className="text-right">
              <p className="text-xs text-white/60">Total Débit</p>
              <p data-testid="recap-total-entrant" className="text-lg font-bold">{fmt(data.total_general_debit)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/60">Total Crédit</p>
              <p data-testid="recap-total-sortant" className="text-lg font-bold">{fmt(data.total_general_credit)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/60">Équilibre</p>
              <p data-testid="recap-equilibre" className={`text-lg font-bold ${data.total_general_debit === data.total_general_credit ? "text-[#7FE7C4]" : "text-amber-300"}`}>
                {data.total_general_debit === data.total_general_credit ? "Équilibré" : "Écart"}
              </p>
            </div>
          </div>
        </div>
      )}

      {data && totalPages > 1 && (
        <div className="no-print mt-4 flex items-center justify-center gap-3">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-precedente">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-[#1D3D3D]">Page {page} / {totalPages} · {data.total_journaux} journaux</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-suivante">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
